import { Injectable, inject } from '@angular/core';
import { Observable, of, map, catchError, switchMap, take } from 'rxjs';
import { GravatarResponse, GravatarService } from "../../../services/gravatar.service";
import { IAppGlobalSettings, ModelService } from "core-client/api";

@Injectable({
	providedIn: 'root'
})
export class AvatarService {
	private readonly gravatarService = inject(GravatarService);
	private readonly modelService = inject(ModelService);

	/**
	 * Get avatar URL with priority:
	 * 1. Internal avatar (if exists)
	 * 2. Gravatar (if exists AND enabled in settings)
	 * 3. null (component will show initials)
	 *
	 */
	getAvatarUrl(
		email: string,
		internalAvatarUrl?: string | null,
		size: number = 40
	): Observable<string | null> {
		// Priority 1: Use internal avatar if available
		if (internalAvatarUrl) {
			return of(internalAvatarUrl);
		}

		// Priority 2: Check Gravatar (only if enabled in settings)
		if (!email) {
			return of(null);
		}

		// SwitchMap to re-check Gravatar whenever settings change
		return this.modelService._appGlobalSettings.appGlobalSettings$.pipe(
			switchMap((settings: IAppGlobalSettings | null) => {
				if (!settings?.profile.gravatar.enabled) {
					return of(null);
				}

				// If Gravatar is enabled, check if it exists
				return this.checkGravatar(email, size).pipe(
					map(gravatarResponse => {
						if (gravatarResponse.exists) {
							return gravatarResponse.url;
						}
						return null;
					})
				);
			}),
			catchError(error => {
				console.error('[AvatarService] Error:', error);
				return of(null);
			})
		);
	}

	/**
	 * Check if Gravatar exists for email
	 * This calls the backend which checks Gravatar.com
	 */
	private checkGravatar(email: string, size: number): Observable<GravatarResponse> {
		return this.gravatarService.getGravatarInfo(email, {
			size
		}).pipe(
			catchError(() => of({
				url: '',
				email,
				cached: false,
				exists: false
			}))
		);
	}

	/**
	 * Prefetch avatar for better performance
	 */
	prefetchAvatar(email: string, internalAvatarUrl?: string | null, size: number = 40): void {
		this.getAvatarUrl(email, internalAvatarUrl, size).pipe(take(1)).subscribe();
	}

	/**
	 * Clear cache
	 */
	clearCache(email?: string): void {
		this.gravatarService.clearCache(email);
	}
}
